import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { format } from "date-fns";

import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import { getTimeZoneShortName } from "~/shared/utils/time-utils.ts";

import { canTenderQuoteBoardRow } from "../utils/quote-board-status.ts";
import styles from "./quote-board-card-batch-header.module.css";

type BoardItem = QuoteBoardItem | CustomerQuoteBoardItem;

type QuoteBoardCardBatchHeaderProps = {
	bulkQuoteId: string;
	items: BoardItem[];
	isInternal?: boolean;
	selectedQuoteIds?: ReadonlySet<number>;
	showBulkTenderSelection?: boolean;
	onToggleBatchSelection?: (
		bulkQuoteId: string,
		selected: boolean,
		visibleQuoteIds: readonly number[],
	) => void;
};

const getBatchCreatedDate = (item: BoardItem): string | null =>
	item.bulkReceivedAtUtc ?? item.createdDate;

const formatBatchCreatedDate = (value: string | null): string => {
	if (!value) return "N/A";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "N/A";
	return `${format(date, "MM/dd/yyyy hh:mm a")} ${getTimeZoneShortName(date)}`;
};

const getEarliestBatchCreatedDate = (items: BoardItem[]): string | null => {
	let earliest: string | null = null;
	for (const item of items) {
		const candidate = getBatchCreatedDate(item);
		if (!candidate) continue;
		if (!earliest || new Date(candidate) < new Date(earliest)) {
			earliest = candidate;
		}
	}
	return earliest;
};

/** Non-grid equivalent of `QuoteBoardGroupHeader`, used above a batch's cards. */
export const QuoteBoardCardBatchHeader = ({
	bulkQuoteId,
	items,
	isInternal = false,
	selectedQuoteIds,
	showBulkTenderSelection = false,
	onToggleBatchSelection,
}: QuoteBoardCardBatchHeaderProps) => {
	const earliestCreatedDate = getEarliestBatchCreatedDate(items);

	const eligibleQuoteIds = items
		.filter((item) => canTenderQuoteBoardRow(item, { isInternal }))
		.map((item) => item.quoteId);
	const allEligibleSelected =
		eligibleQuoteIds.length > 0 &&
		eligibleQuoteIds.every((quoteId) => selectedQuoteIds?.has(quoteId));

	const requestCount = items[0]?.requestCount ?? 0;
	const processedCount = items[0]?.processedCount ?? 0;
	const errorCount = items[0]?.errorCount ?? 0;

	return (
		<div
			className={styles.batchHeader}
			data-testid={"quote-board-batch-header"}
		>
			{showBulkTenderSelection ? (
				<Checkbox
					aria-label={`Select all eligible shipments in batch ${bulkQuoteId}`}
					checked={allEligibleSelected}
					disabled={eligibleQuoteIds.length === 0}
					onChange={(event) =>
						onToggleBatchSelection?.(bulkQuoteId, event.value, eligibleQuoteIds)
					}
				/>
			) : null}
			<span className={styles.batchDate}>
				{formatBatchCreatedDate(earliestCreatedDate)}
			</span>
			{requestCount > 0 ? (
				<span className={styles.batchProgress}>
					{processedCount}/{requestCount} processed
					{errorCount > 0 ? `, ${errorCount} errors` : ""}
				</span>
			) : null}
		</div>
	);
};
